import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ReAuthModal } from "@/components/auth/ReAuthModal";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Pagination } from "@/components/ui/pagination";
import {
  Plus,
  Edit2,
  Mail,
  X,
  UserCheck,
  UserX,
  Ban,
  Users,
  UserPlus,
  UserMinus,
  Trash2,
  ShieldOff,
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
} from "lucide-react";
import { staffApi, type Staff } from "@/lib/api/staff";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

const StaffManagementPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "name-asc" | "name-desc" | "email-asc" | "email-desc" | "role" | "branch" | "status"
  >("name-asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"resend" | "revoke" | null>(null);
  const [staffForAction, setStaffForAction] = useState<Staff | null>(null);
  const [processing, setProcessing] = useState(false);

  // Re-auth state for sensitive actions (activate, deactivate, suspend, delete)
  const [reAuthOpen, setReAuthOpen] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [reAuthLoading, setReAuthLoading] = useState(false);
  const [reAuthActionType, setReAuthActionType] = useState<
    "activate" | "deactivate" | "suspend" | "delete" | null
  >(null);
  const [staffForReAuth, setStaffForReAuth] = useState<Staff | null>(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPassword, setImportPassword] = useState("");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    failed: Array<{ row: number; email: string; error: string }>;
  } | null>(null);

  useEffect(() => {
    if (isTenantAdmin) {
      loadStaff();
    }
  }, [isTenantAdmin]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const data = await staffApi.getAll();
      setStaff(data);
    } catch (error) {
      console.error("Failed to load staff:", error);
      toast.error("Unable to load staff. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (staffMember: Staff) => {
    navigate(`/setup/staff/${staffMember.id}/edit`);
  };

  const openActionDialog = (staffMember: Staff, type: "resend" | "revoke") => {
    setStaffForAction(staffMember);
    setActionType(type);
    setActionDialogOpen(true);
  };

  const openReAuthAction = (
    staffMember: Staff,
    type: "activate" | "deactivate" | "suspend" | "delete"
  ) => {
    setStaffForReAuth(staffMember);
    setReAuthActionType(type);
    setReAuthPassword("");
    setReAuthOpen(true);
  };

  const handleAction = async () => {
    if (!staffForAction || !actionType) return;

    try {
      setProcessing(true);
      switch (actionType) {
        case "resend":
          await staffApi.resendInvite(staffForAction.id);
          toast.success(`Invitation resent to ${staffForAction.email}`);
          break;
        case "revoke":
          await staffApi.revokeInvite(staffForAction.id);
          toast.success(`Invitation revoked for ${staffForAction.email}`);
          break;
      }
      await loadStaff();
      setActionDialogOpen(false);
      setStaffForAction(null);
      setActionType(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to perform action. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      await staffApi.exportCsv();
      toast.success("Staff list exported successfully.");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to export staff.");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    staffApi.downloadTemplate();
    toast.success("Template downloaded.");
  };

  const handleImportSubmit = async () => {
    if (!importFile || !importPassword) {
      toast.error("Please select a CSV file and enter your password.");
      return;
    }
    try {
      setImporting(true);
      setImportResult(null);
      const result = await staffApi.importCsv(importFile, importPassword);
      setImportResult(result);
      if (result.failed.length > 0) {
        toast.info(
          `Imported ${result.created} staff. ${result.failed.length} row(s) failed—see details below.`
        );
      } else {
        toast.success(
          `Successfully imported ${result.created} staff member(s). Invitation emails have been sent.`
        );
        setImportModalOpen(false);
        resetImportState();
      }
      loadStaff();
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error("Invalid password. Please try again.");
      } else {
        toast.error(error.response?.data?.message || "Import failed.");
      }
    } finally {
      setImporting(false);
    }
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportPassword("");
    setImportResult(null);
  };

  const handleReAuthConfirm = async () => {
    if (!staffForReAuth || !reAuthActionType || !reAuthPassword) return;

    try {
      setReAuthLoading(true);
      const fullName = `${staffForReAuth.firstName} ${staffForReAuth.lastName}`;
      switch (reAuthActionType) {
        case "activate":
          await staffApi.activate(staffForReAuth.id, reAuthPassword);
          toast.success(`${fullName} has been activated`);
          break;
        case "deactivate":
          await staffApi.deactivate(staffForReAuth.id, reAuthPassword);
          toast.success(`${fullName} has been deactivated`);
          break;
        case "suspend":
          await staffApi.suspend(staffForReAuth.id, reAuthPassword);
          toast.success(`${fullName} has been suspended`);
          break;
        case "delete":
          await staffApi.deleteStaff(staffForReAuth.id, reAuthPassword);
          toast.success(`${fullName} has been permanently deleted`);
          break;
      }
      await loadStaff();
      setReAuthOpen(false);
      setReAuthPassword("");
      setStaffForReAuth(null);
      setReAuthActionType(null);
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error("Incorrect password. Please try again.");
      } else {
        toast.error(error.response?.data?.message || "Unable to perform action. Please try again.");
      }
    } finally {
      setReAuthLoading(false);
    }
  };

  const getStatusBadge = (status: Staff["status"]) => {
    const badges = {
      PENDING: "bg-yellow-100 text-yellow-700",
      ACTIVE: "bg-green-100 text-green-700",
      INACTIVE: "bg-gray-200 text-gray-600",
      SUSPENDED: "bg-red-100 text-red-700",
    };
    return badges[status] || badges.INACTIVE;
  };

  const filteredStaff = useMemo(() => {
    const filtered = staff.filter((member) => {
      const matchesSearch =
        member.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.staffId?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === "all" || member.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case "name-desc":
          return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        case "email-asc":
          return a.email.localeCompare(b.email);
        case "email-desc":
          return b.email.localeCompare(a.email);
        case "role":
          return (a.role?.name ?? "").localeCompare(b.role?.name ?? "");
        case "branch":
          return (a.branch?.name ?? "").localeCompare(b.branch?.name ?? "");
        case "status":
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
  }, [staff, searchQuery, statusFilter, sortBy]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sortBy]);

  // Paginate the filtered results
  const paginatedStaff = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStaff.slice(startIndex, endIndex);
  }, [filteredStaff, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);

  const stats = {
    total: staff.length,
    pending: staff.filter((s) => s.status === "PENDING").length,
    active: staff.filter((s) => s.status === "ACTIVE").length,
    inactive: staff.filter((s) => s.status === "INACTIVE").length,
    suspended: staff.filter((s) => s.status === "SUSPENDED").length,
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage staff.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Staff Management"
          description="Manage library staff members, invitations, and permissions"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Staff" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, email, or staff ID..."
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-5">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Staff</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-50">
                <Mail className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-50">
                <UserMinus className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-50">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.suspended}</p>
                <p className="text-sm text-gray-500">Suspended</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="name-asc">Name (A–Z)</option>
              <option value="name-desc">Name (Z–A)</option>
              <option value="email-asc">Email (A–Z)</option>
              <option value="email-desc">Email (Z–A)</option>
              <option value="role">Role</option>
              <option value="branch">Branch</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={exporting || staff.length === 0}
              className="gap-1.5"
            >
              <Download className="w-4 h-4" />
              {exporting ? "Exporting..." : "Export"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImportModalOpen(true)}
              className="gap-1.5"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            <Button onClick={() => navigate("/setup/staff/new")} className="shadow-sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Staff Member
            </Button>
          </div>
        </div>

        {/* Staff List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading staff..." />
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching staff" : "No staff members yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term."
                  : "Add your first staff member to get started."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/setup/staff/new")} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Staff Member
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {paginatedStaff.map((member) => (
                  <div key={member.id} className="p-5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3
                            className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/setup/staff/${member.id}`)}
                          >
                            {member.firstName} {member.lastName}
                          </h3>
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded-full text-xs font-medium",
                              getStatusBadge(member.status)
                            )}
                          >
                            {member.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span>{member.email}</span>
                          {member.staffId && <span>ID: {member.staffId}</span>}
                          {member.role && <span>Role: {member.role.name}</span>}
                          {member.branch && <span>Branch: {member.branch.name}</span>}
                          {member.department && <span>Dept: {member.department.name}</span>}
                          {member.jobTitle && <span>Title: {member.jobTitle.name}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {member.status === "PENDING" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openActionDialog(member, "resend")}
                              className="gap-1.5"
                            >
                              <Mail className="w-4 h-4" />
                              <span className="hidden sm:inline">Resend</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openActionDialog(member, "revoke")}
                              className="gap-1.5"
                            >
                              <X className="w-4 h-4" />
                              <span className="hidden sm:inline">Revoke</span>
                            </Button>
                          </>
                        )}
                        {member.status === "ACTIVE" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReAuthAction(member, "deactivate")}
                              className="gap-1.5"
                            >
                              <UserX className="w-4 h-4" />
                              <span className="hidden sm:inline">Deactivate</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReAuthAction(member, "suspend")}
                              className="gap-1.5 text-red-600"
                            >
                              <Ban className="w-4 h-4" />
                              <span className="hidden sm:inline">Suspend</span>
                            </Button>
                          </>
                        )}
                        {(member.status === "INACTIVE" || member.status === "SUSPENDED") && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReAuthAction(member, "activate")}
                              className="gap-1.5"
                            >
                              <UserCheck className="w-4 h-4" />
                              <span className="hidden sm:inline">Activate</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReAuthAction(member, "delete")}
                              className="gap-1.5 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Delete</span>
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(member)}
                          className="gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredStaff.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredStaff.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </PageContainer>

      {/* Confirm Dialog for non-sensitive actions (resend, revoke) */}
      <ConfirmDialog
        open={actionDialogOpen}
        onOpenChange={setActionDialogOpen}
        title={
          actionType === "resend"
            ? `Resend invitation to ${staffForAction?.email}?`
            : actionType === "revoke"
              ? `Revoke invitation for ${staffForAction?.email}?`
              : "Confirm Action"
        }
        description={
          actionType === "resend"
            ? "A new invitation email will be sent to this staff member."
            : actionType === "revoke"
              ? "This will invalidate the current invitation. You can send a new one later."
              : ""
        }
        confirmLabel={
          actionType === "resend" ? "Resend" : actionType === "revoke" ? "Revoke" : "Confirm"
        }
        cancelLabel="Cancel"
        onConfirm={handleAction}
        variant={actionType === "revoke" ? "danger" : "info"}
        loading={processing}
      />

      {/* Import Staff Modal */}
      <Dialog
        open={importModalOpen}
        onOpenChange={(open) => {
          setImportModalOpen(open);
          if (!open) resetImportState();
        }}
      >
        <DialogContent
          className="sm:max-w-lg"
          onClose={() => {
            setImportModalOpen(false);
            resetImportState();
          }}
        >
          <DialogHeader>
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-primary/10">
                <FileSpreadsheet className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Import Staff from CSV
                </DialogTitle>
                <DialogDescription className="mt-2 text-sm text-gray-600">
                  Upload a CSV file with staff details. Use exact names for department, job title,
                  and role; use branch code for branch assignment. Invitation emails are sent
                  automatically to each new staff member.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {importResult && importResult.failed.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Imported {importResult.created} staff. {importResult.failed.length} row(s)
                    failed.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Fix the issues below in your CSV and try again.
                  </p>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
                <ul className="space-y-3 text-sm">
                  {importResult.failed.map((f, i) => (
                    <li
                      key={i}
                      className="flex flex-col gap-1 border-b border-gray-200 pb-3 last:border-0 last:pb-0 last:mb-0"
                    >
                      <span className="font-medium text-gray-900">
                        Row {f.row}: {f.email}
                      </span>
                      <span className="text-red-600 text-xs">{f.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <DialogFooter className="mt-6 flex gap-3 sm:gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportResult(null);
                    setImportFile(null);
                    setImportPassword("");
                  }}
                  className="flex-1 sm:flex-none"
                >
                  Import Another File
                </Button>
                <Button onClick={() => setImportModalOpen(false)} className="flex-1 sm:flex-none">
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download CSV Template
                </Button>

                <div>
                  <Label htmlFor="import-file" className="text-sm font-medium text-gray-700">
                    CSV File
                  </Label>
                  <div
                    className={cn(
                      "mt-1.5 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors",
                      importFile
                        ? "border-green-500 bg-green-50"
                        : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                    )}
                    onClick={() => document.getElementById("import-file")?.click()}
                  >
                    <input
                      id="import-file"
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                      className="hidden"
                    />
                    <Upload className="w-10 h-10 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      {importFile ? (
                        <span className="font-medium text-green-700">{importFile.name}</span>
                      ) : (
                        <>
                          <span className="font-medium text-primary">Click to upload</span> or drag
                          and drop
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">CSV only</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary">Re-authentication required</p>
                    <p className="text-xs text-primary mt-1">
                      Enter your password to confirm this import. Your password is not stored.
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="import-password" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <PasswordInput
                    id="import-password"
                    value={importPassword}
                    onChange={(e) => setImportPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="mt-1.5"
                    disabled={importing}
                  />
                </div>
              </div>
              <DialogFooter className="mt-6 flex gap-3 sm:gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportModalOpen(false);
                    resetImportState();
                  }}
                  disabled={importing}
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImportSubmit}
                  disabled={!importFile || !importPassword || importing}
                  className="flex-1 sm:flex-none gap-2"
                >
                  {importing ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Re-Auth Modal for sensitive actions (activate, deactivate, suspend, delete) */}
      <ReAuthModal
        open={reAuthOpen}
        onOpenChange={(v) => {
          setReAuthOpen(v);
          if (!v) {
            setReAuthPassword("");
            setStaffForReAuth(null);
            setReAuthActionType(null);
          }
        }}
        onConfirm={handleReAuthConfirm}
        password={reAuthPassword}
        onPasswordChange={setReAuthPassword}
        loading={reAuthLoading}
        title={
          reAuthActionType === "activate"
            ? `Activate ${staffForReAuth?.firstName} ${staffForReAuth?.lastName}`
            : reAuthActionType === "deactivate"
              ? `Deactivate ${staffForReAuth?.firstName} ${staffForReAuth?.lastName}`
              : reAuthActionType === "suspend"
                ? `Suspend ${staffForReAuth?.firstName} ${staffForReAuth?.lastName}`
                : reAuthActionType === "delete"
                  ? `Permanently Delete ${staffForReAuth?.firstName} ${staffForReAuth?.lastName}`
                  : "Confirm Action"
        }
        description={
          reAuthActionType === "activate"
            ? "This staff member will be able to log in and access the system. Enter your password to confirm."
            : reAuthActionType === "deactivate"
              ? "This staff member will no longer be able to log in. Enter your password to confirm."
              : reAuthActionType === "suspend"
                ? "This staff member will be temporarily suspended from accessing the system. Enter your password to confirm."
                : reAuthActionType === "delete"
                  ? "This will permanently remove this staff member from the system. This action cannot be undone. Enter your password to confirm."
                  : "Enter your password to confirm."
        }
      />
    </AppLayout>
  );
};

export default StaffManagementPage;
